import { useState } from 'react';
import { Text, TouchableOpacity, View } from 'react-native';
import { AuthChrome } from '../components/AuthChrome';
import { AuthField } from '../components/ui';
import type { Language, Screen, Translation } from '../types';
import { styles } from '../theme';

const REGISTER_COUNTRIES = [
  { value: 'UAE', labelKey: 'countryUae' as const },
  { value: 'Oman', labelKey: 'countryOman' as const },
] as const;

function isValidEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

export function CreateAccountScreen({
  t,
  language,
  setLanguage,
  onRegister,
  setScreen,
}: {
  t: Translation;
  language: Language;
  setLanguage: (language: Language) => void | Promise<void>;
  onRegister: (values: {
    name: string;
    email: string;
    password: string;
    city: string;
    country: string;
    homeClub: string;
    preferredLanguage: Language;
  }) => Promise<void>;
  setScreen?: (screen: Screen) => void;
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState<(typeof REGISTER_COUNTRIES)[number]['value']>('UAE');
  const [homeClub, setHomeClub] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const trimmedName = name.trim();
  const trimmedEmail = email.trim().toLowerCase();
  const trimmedCity = city.trim();
  const trimmedHomeClub = homeClub.trim();
  const emailValid = trimmedEmail.length > 0 && isValidEmail(trimmedEmail);
  const passwordValid = password.length >= 8;
  const confirmValid = confirmPassword.length > 0 && password === confirmPassword;

  async function handleRegister() {
    if (!trimmedName || !trimmedEmail || !password || !confirmPassword || !trimmedCity || !trimmedHomeClub) {
      setError(t.registerRequiredFields);
      return;
    }
    if (!emailValid) {
      setError(t.registerInvalidEmail);
      return;
    }
    if (!passwordValid) {
      setError(t.registerPasswordTooShort);
      return;
    }
    if (!confirmValid) {
      setError(t.registerPasswordMismatch);
      return;
    }

    setError('');
    setSubmitting(true);
    try {
      await onRegister({
        name: trimmedName,
        email: trimmedEmail,
        password,
        city: trimmedCity,
        country,
        homeClub: trimmedHomeClub,
        preferredLanguage: language,
      });
    } catch (registerError) {
      setError(registerError instanceof Error ? registerError.message : t.registerRequiredFields);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthChrome t={t} setScreen={setScreen}>
      <Text style={styles.subtitle}>{t.registerSubtitle}</Text>
      <View style={styles.authForm}>
        <AuthField label={t.fullNameLabel.toUpperCase()} value={name} onChangeText={setName} valid={trimmedName.length >= 2} autoCapitalize="words" showLabel={t.showPassword} hideLabel={t.hidePassword} />
        <AuthField label={t.emailFieldLabel.toUpperCase()} value={email} onChangeText={setEmail} valid={emailValid} keyboardType="email-address" autoCapitalize="none" showLabel={t.showPassword} hideLabel={t.hidePassword} />
        <AuthField label={t.passwordFieldLabel.toUpperCase()} value={password} onChangeText={setPassword} secureTextEntry valid={passwordValid} showLabel={t.showPassword} hideLabel={t.hidePassword} />
        <AuthField
          label={t.confirmPassword.toUpperCase()}
          value={confirmPassword}
          onChangeText={setConfirmPassword}
          secureTextEntry
          valid={confirmValid}
          showLabel={t.showPassword}
          hideLabel={t.hidePassword}
        />
        <AuthField label={t.cityFieldLabel.toUpperCase()} value={city} onChangeText={setCity} valid={trimmedCity.length >= 2} autoCapitalize="words" showLabel={t.showPassword} hideLabel={t.hidePassword} />
        <View>
          <Text style={styles.inputLabel}>{t.registerCountry.toUpperCase()}</Text>
          <View style={styles.countryPills}>
            {REGISTER_COUNTRIES.map(option => {
              const active = country === option.value;
              return (
                <TouchableOpacity
                  key={option.value}
                  style={[styles.countryPill, active && styles.countryPillActive]}
                  onPress={() => setCountry(option.value)}
                >
                  <Text style={[styles.countryPillText, active && styles.countryPillTextActive]}>{t[option.labelKey]}</Text>
                </TouchableOpacity>
              );
            })}
          </View>
        </View>
        <AuthField label={t.homeClubFieldLabel.toUpperCase()} value={homeClub} onChangeText={setHomeClub} valid={trimmedHomeClub.length >= 2} autoCapitalize="words" showLabel={t.showPassword} hideLabel={t.hidePassword} />
        <View style={styles.settingsGroup}>
          <View style={styles.settingsRow}>
            <Text style={styles.cardTitle}>{t.language}</Text>
            <TouchableOpacity onPress={() => void setLanguage(language === 'en' ? 'ru' : 'en')}>
              <Text style={styles.points}>{language.toUpperCase()}</Text>
            </TouchableOpacity>
          </View>
        </View>
        {error ? <Text style={styles.formErrorText}>{error}</Text> : null}
        <TouchableOpacity disabled={submitting} style={styles.primaryButton} onPress={() => void handleRegister()}>
          <Text style={styles.primaryButtonText}>{submitting ? t.submittingLabel : t.register}</Text>
        </TouchableOpacity>
        {setScreen && (
          <TouchableOpacity style={styles.secondaryButton} onPress={() => setScreen('welcome')}>
            <Text style={styles.secondaryButtonText}>{t.signIn}</Text>
          </TouchableOpacity>
        )}
      </View>
    </AuthChrome>
  );
}
